import { useEffect, useState } from "react";
import { theme, card, emptyFrame, type as text } from "../theme";
import { StandardHeader, STANDARD_PAGE_LABELS } from "../components/standards/StandardHeader";
import { api } from "../api/client";
import { useExperience } from "../context/ExperienceContext";
import { PortalLink } from "../components/PortalLink";
import { FrameworkControlList } from "../components/FrameworkControlList";
import type { SoaRow, EvidenceDoc } from "../components/FrameworkControlList";
import { buildInsightsMap } from "../components/ControlInsightsPanel";
import type { ControlInsights } from "../components/ControlInsightsPanel";
import { SoaFrameworkPanel } from "../components/SoaFrameworkPanel";

// NCSC Cyber Assessment Framework v4.0. Laid out as the other standards are: an assessment page
// (the objectives and their 41 contributing outcomes, grouped and filterable, each with its
// AI insight and evidence — the ISO clause pages' pattern) and a separate "What applies (SoA)"
// page. Outcomes are seeded as CONTROL rows `ncsc-caf-<outcome>` by backend/src/lib/ncscCaf.ts;
// the CAF's own scale maps onto the platform statuses as the legend shows.

interface Principle { id: string; name: string; question: string }
interface Objective { id: "A" | "B" | "C" | "D"; name: string; summary: string; principles: Principle[] }

const OBJECTIVES: Objective[] = [
  {
    id: "A", name: "Managing security risk",
    summary: "Appropriate organisational structures, policies and processes to understand, assess and systematically manage security risks to network and information systems supporting essential functions.",
    principles: [
      { id: "A1", name: "Governance", question: "Does the board own security risk to essential functions, with clear roles, and does it take decisions on that risk?" },
      { id: "A2", name: "Risk management", question: "Is risk to essential functions identified, assessed and treated through a process the organisation can show working?" },
      { id: "A3", name: "Asset management", question: "Is everything the essential functions rely on — systems, data, people, suppliers — known and recorded?" },
      { id: "A4", name: "Supply chain", question: "Are the security risks from suppliers to essential functions understood and managed?" },
    ],
  },
  {
    id: "B", name: "Protecting against cyber attack",
    summary: "Proportionate security measures to protect the network and information systems supporting essential functions from cyber attack.",
    principles: [
      { id: "B1", name: "Policies, processes and procedures", question: "Are security policies defined, communicated to staff and shown to be followed?" },
      { id: "B2", name: "Identity and access control", question: "Is access to essential-function systems limited to identified, authenticated and authorised people and devices?" },
      { id: "B3", name: "Data security", question: "Is data important to essential functions protected in transit, at rest and on devices, and do you know where it is?" },
      { id: "B4", name: "System security", question: "Are systems securely configured, patched and protected against exploitation?" },
      { id: "B5", name: "Resilient networks and systems", question: "Can the essential functions withstand and recover from disruption, with tested plans and objectives?" },
      { id: "B6", name: "Staff awareness and training", question: "Do staff understand their part in security, and is that awareness trained and measured?" },
    ],
  },
  {
    id: "C", name: "Detecting cyber security events",
    summary: "Capabilities to ensure security defences remain effective and to detect cyber security events affecting, or with the potential to affect, essential functions.",
    principles: [
      { id: "C1", name: "Security monitoring", question: "Are the right sources monitored, and are alerts triaged and acted on?" },
      { id: "C2", name: "Threat hunting", question: "Does the organisation proactively look for attacks that monitoring would miss?" },
    ],
  },
  {
    id: "D", name: "Minimising the impact of cyber security incidents",
    summary: "Capabilities to minimise the adverse impact of a cyber security incident on the operation of essential functions, including the restoration of those functions where necessary.",
    principles: [
      { id: "D1", name: "Response and recovery planning", question: "Is there a tested incident response and recovery plan covering the essential functions?" },
      { id: "D2", name: "Lessons learned", question: "Are the root causes of incidents found, and does the organisation improve because of them?" },
    ],
  },
];


const outcomePrinciple = (controlId: string) => controlId.replace(/^ncsc-caf-/, "").split(".")[0];
const PRINCIPLE_BY_ID = new Map(OBJECTIVES.flatMap((o) => o.principles.map((p) => [p.id, p] as const)));

export const CAF_SCALE: Array<[string, string]> = [["Achieved", "Compliant"], ["Partially achieved", "In progress"], ["Not achieved", "Gap identified"], ["Not assessed", "Not assessed"]];

/** How the CAF's own achievement scale reads in the platform's statuses. */
export function CafScaleLegend() {
  return (
    <div style={{ display: "flex", gap: 14, flexWrap: "wrap", ...text.hint, marginBottom: 16 }}>
      <span style={{ fontWeight: 600, color: theme.color.text }}>CAF scale:</span>
      {CAF_SCALE.map(([caf, status]) => <span key={caf}>{caf} = <em>{status}</em></span>)}
    </div>
  );
}

export function NcscCafPage() {
  const { novice } = useExperience();
  const [rows, setRows] = useState<SoaRow[] | null>(null);
  const [docs, setDocs] = useState<EvidenceDoc[]>([]);
  const [insightsMap, setInsightsMap] = useState<Map<string, ControlInsights>>(new Map());

  async function loadAll() {
    const [soaData, docsData, latestRun] = await Promise.all([
      api.get<{ soa: SoaRow[] }>("/controls/soa"),
      api.get<{ documents: EvidenceDoc[] }>("/documents").catch(() => ({ documents: [] as EvidenceDoc[] })),
      api.get<{ run: { resultJson: unknown } | null }>("/guidance/latest").catch(() => ({ run: null })),
    ]);
    setRows(soaData.soa.filter((r) => r.control.frameworkId === "ncsc_caf"));
    setDocs(docsData.documents);
    if (latestRun.run?.resultJson) setInsightsMap(buildInsightsMap(latestRun.run.resultJson));
  }

  useEffect(() => { void loadAll(); }, []);

  const tally = (objectiveId: string) => {
    const g = rows?.filter((r) => outcomePrinciple(r.control.id).startsWith(objectiveId)) ?? [];
    return { achieved: g.filter((r) => r.entry.status === "COMPLIANT").length, total: g.length };
  };

  // Each outcome carries its principle's key question as guidance in the detail.
  const withGuidance = (rows ?? []).map((r) => {
    const p = PRINCIPLE_BY_ID.get(outcomePrinciple(r.control.id));
    return p ? { ...r, control: { ...r.control, guidance: `Principle ${p.id} ${p.name}. Key question: ${p.question}` } } : r;
  });

  return (
    <div className="page-pad">
      <StandardHeader
        frameworkId="ncsc_caf"
        page={STANDARD_PAGE_LABELS.cafOutcomes}
        subtitle="Assess each of the 41 contributing outcomes against its indicators of good practice and attach the evidence."
      >
        <p style={{ ...text.hint, margin: "6px 0 0", maxWidth: 820 }}>
          The <PortalLink to="/resilience" style={{ color: theme.color.accentText }}>resilience registers</PortalLink> produce the records objectives A3, B5, B6, C and D ask for.
          Decide which outcomes apply on <PortalLink to="/ncsc-caf-soa" style={{ color: theme.color.accentText }}>{STANDARD_PAGE_LABELS.soa}</PortalLink>.
        </p>
      </StandardHeader>

      {/* The four objectives: what each one covers and how far the organisation has got */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 8, marginBottom: 20 }}>
        {OBJECTIVES.map((o) => {
          const t = tally(o.id);
          return (
            <div key={o.id} title={o.summary} style={{ ...card, padding: "10px 14px" }}>
              <div style={{ ...text.itemTitle, fontSize: text.body.fontSize, marginBottom: 2 }}>
                Objective {o.id}: {o.name}{t.total ? <span style={{ ...text.meta, fontWeight: 600, marginLeft: 6 }}>{t.achieved}/{t.total} achieved</span> : null}
              </div>
              <div style={text.meta}>{o.principles.map((p) => `${p.id} ${p.name}`).join(" · ")}</div>
            </div>
          );
        })}
      </div>

      <CafScaleLegend />

      {rows === null ? (
        <div style={text.hint}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ ...emptyFrame, padding: "24px 20px" }}>
          <div style={{ ...text.itemTitle, marginBottom: 6 }}>No outcomes to show</div>
          <div style={text.hint}>The CAF outcomes appear here once the organisation is enrolled in the NCSC CAF and the reference data is loaded.</div>
        </div>
      ) : (
        <FrameworkControlList
          rows={withGuidance}
          docs={docs}
          frameworkId="ncsc_caf"
          loadAll={loadAll}
          novice={novice}
          insightsMap={insightsMap}
          noun="outcome"
          summaryLabel="Outcomes achieved"
          groupLabel="All principles"
        />
      )}
    </div>
  );
}

export function NcscCafSoaPage() {
  return (
    <div className="page-pad">
      <StandardHeader
        frameworkId="ncsc_caf"
        page={STANDARD_PAGE_LABELS.soa}
        subtitle="Decide which contributing outcomes apply to your essential functions, record why, and attach evidence."
      />
      <CafScaleLegend />
      <SoaFrameworkPanel frameworkId="ncsc_caf" noun="outcome" />
    </div>
  );
}
